import { SxProps } from "@outschool/backpack";
import { ClassDetailsQuery_ActivityFragment } from "@outschool/gql-frontend-generated";
import { addParamsToUrl, createUrlWithActivity } from "@outschool/routes";
import {
  ModalContext,
  OutreachModal,
  OutreachModalType,
  ShareButton,
  ShareContent,
  ShareContentType,
  ShareMenu,
  ShareMenuProps,
} from "@outschool/ui-components-website";
import React, { useState } from "react";

import * as Env from "../../../../shared/Env";

type ActivityShareMenuProps = ShareMenuProps &
  Omit<ShareContentType, "shareLink" | "modalContext" | "activity"> & {
    user?: {
      slug_id: string;
      teacher_approved_at: Date | null;
      suspended_at: Date | null;
    } | null;
    campaignPrefix?: string;
    activity: Pick<
      ClassDetailsQuery_ActivityFragment,
      | "uid"
      | "isClub"
      | "slug_id"
      | "title"
      | "leader"
      | "details"
      | "is_ongoing_weekly"
      | "price_cents"
      | "duration_weeks"
      | "size_max"
      | "weekly_meetings"
      | "hasTeacherSchedule"
      | "duration_minutes"
      | "isIncludedWithMembership"
    >;
    buttonSx?: SxProps;
  };

export default function ActivityShareMenu({
  size,
  user,
  activity,
  label,
  iconOnly,
  campaignPrefix = "share_activity",
  linkColor,
  buttonSx,
}: ActivityShareMenuProps) {
  const shareLink = addParamsToUrl(createUrlWithActivity(activity), {
    usid: user && user.slug_id,
    signup: true,
    utm_campaign: `${campaignPrefix}_link`,
  });
  const [showShareModal, setShowShareModal] = useState(false);

  const trackingMetaData = { activity_uid: activity.uid };
  const isEducator = !!user?.teacher_approved_at && !user?.suspended_at;

  if (isEducator) {
    return (
      <>
        <ShareButton
          iconOnly={iconOnly}
          variant={"link"}
          size={size}
          label={label}
          hideLabelOnMobile={true}
          onClick={() => setShowShareModal(true)}
          linkColor={linkColor}
          trackingMetaData={trackingMetaData}
          sx={{ padding: "1em", ...buttonSx }}
        />
        <OutreachModal
          open={showShareModal}
          onClose={() => setShowShareModal(false)}
          outreachShareUrl={shareLink}
          modalType={{
            variant: OutreachModalType.class,
            activity,
          }}
        />
      </>
    );
  }

  return (
    <ShareMenu
      size={size}
      label={label}
      hideLabelOnMobile={true}
      iconOnly={iconOnly}
      trackingMetaData={trackingMetaData}
      linkColor={linkColor}
      variant={"link"}
      buttonSx={{ padding: "1em", ...buttonSx }}
    >
      <ShareContent
        activity={activity}
        shareLink={shareLink}
        trackingMetaData={trackingMetaData}
        modalContext={ModalContext.UnenrolledClass}
        facebookAppId={Env.FACEBOOK_APP_ID}
      />
    </ShareMenu>
  );
}
